import React, { useEffect, useState } from 'react'
import { ConfigProvider } from 'antd'
import ruRU from 'antd/locale/ru_RU'
import { theme } from '../styles/theme'
import { useMediaQuery } from 'react-responsive'
import { breakpoints } from '../styles/breakpoints'

interface AntdProviderProps {
  children: React.ReactNode
}

export const AntdProvider: React.FC<AntdProviderProps> = ({ children }) => {
  const isXs = useMediaQuery({ maxWidth: breakpoints.sm - 1 })

  const [key, setKey] = useState(0)

  useEffect(() => {
    const handleOrientationChange = () => {
      setKey((prev) => prev + 1)
    }

    window.addEventListener('orientationchange', handleOrientationChange)
    return () =>
      window.removeEventListener('orientationchange', handleOrientationChange)
  }, [])

  return (
    <ConfigProvider
      key={key}
      locale={ruRU}
      theme={{
        token: {
          fontFamily: '"Inter", Roboto, sans-serif',
          fontSize: 14,
          lineHeight: 1.5,
          colorTextHeading: theme.colors.text,
          colorText: theme.colors.text,
          colorPrimary: theme.colors.primary,
          colorError: theme.colors.error,
          colorSuccess: theme.colors.success,
          colorWarning: theme.colors.warning,
          colorBorder: theme.colors.border,
          colorBgBase: theme.colors.background,
          colorTextSecondary: theme.colors.textLight,
        },
        components: {
          Button: {
            controlHeight: 40,
            controlHeightSM: 32,
            controlHeightLG: 48,
          },
          Input: {
            controlHeight: 40,
            controlHeightSM: 32,
            controlHeightLG: 48,
          },
          Typography: {
            titleMarginBottom: '0',
            titleMarginTop: '0',
            fontSizeHeading1: 32,
            lineHeightHeading1: 1.5,
            fontSizeHeading2: !isXs ? 24 : 20,
            lineHeightHeading2: 1.5,
            fontSizeHeading3: 20,
            lineHeightHeading3: 1.5,
            fontSizeHeading4: 16,
            lineHeightHeading4: 1.5,
            fontSizeHeading5: 14,
            lineHeightHeading5: 1.428,
            colorTextSecondary: theme.colors.textLight,
            colorTextDescription: theme.colors.textLight,
            colorLink: theme.colors.link,
            colorLinkHover: theme.colors.link,
          },
          Checkbox: {
            colorBorder: theme.colors.checkbox.border,
            borderRadiusSM: 3,
          },
          Alert: {
            colorInfoBg: theme.colors.alert.info.bg,
            colorInfoBorder: theme.colors.alert.info.border,
            colorErrorBg: theme.colors.alert.error.bg,
            colorErrorBorder: theme.colors.alert.error.border,
            colorWarningBg: theme.colors.alert.warning.bg,
            colorWarningBorder: theme.colors.alert.warning.border,
          },
          Divider: {
            colorSplit: theme.colors.divider,
            marginLG: 0,
          },
          Radio: {
            colorBorder: theme.colors.radio.border,
          },
          Progress: {
            defaultColor: theme.colors.progress.fill,
            remainingColor: theme.colors.progress.bg,
            colorSuccess: theme.colors.progress.fill,
          },
          Table: {
            headerBg: theme.colors.white,
            headerColor: theme.colors.cardDescription,
            cellPaddingBlockSM: 9,
            borderColor: theme.colors.divider,
            headerBorderRadius: 0,
          },
          Tabs: {
            horizontalMargin: '0',
            cardGutter: 4,
            cardBg: 'transparent',
            colorBorderSecondary: 'transparent',
            borderRadiusLG: 6,
            itemColor: theme.colors.cardDescription,
            itemHoverColor: theme.colors.text,
            itemSelectedColor: theme.colors.text,
            cardPadding: '9px 12px 9px 16px',
          },
          Tooltip: {
            colorBgSpotlight: theme.colors.white,
            colorTextLightSolid: theme.colors.text,
            fontSize: 12,
            boxShadowSecondary: '0px 4px 16px 0px #0C10181F',
            controlHeight: 26,
            paddingSM: 8,
            sizePopupArrow: 12,
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}
